/* eslint-disable react/require-default-props, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import _ from 'underscore';

import { Dialog, DialogFooter, PrimaryButton } from '@bingads-webui-react/fabric-control';
import { withLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Scenario, Activity, withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { withUserGovernance } from './with-user-governance';
import { defaultUserGovernanceConfig } from './user-governance-config';
import { PreferenceState } from './preference-state';
import { getStyles } from './styles';
import { useLogger } from './logger';
import type {
  IActivity, IUserGovernanceConfig,
} from './types';

export interface ISystemAnnouncementRawProps {
  activity?: IActivity;
  classes?: {
    dialogFooter: string;
  };
  featureName: string;
  title: string;
  subTitle?: string;
  content: string;
  primaryButtonText: string;
  onDone: () => void;
  onDismiss: () => void;
}

export type SystemAnnouncementProps = Omit<
ISystemAnnouncementRawProps,
'activity' | 'classes'
> & {
  activity?: IActivity;
  userGovernanceConfig?: IUserGovernanceConfig;
};

const DialogWithMinHeight = Dialog as React.ComponentType<
React.ComponentProps<typeof Dialog> & { minHeight?: number }
>;

const SystemAnnouncementRaw = ({
  activity,
  classes,
  featureName,
  title,
  subTitle,
  content,
  primaryButtonText,
  onDone,
  onDismiss,
}: ISystemAnnouncementRawProps) => {
  const { logTrace } = useLogger(activity, featureName);

  useEffect(() => {
    logTrace('system announcement show');
  }, []);

  const [isDialogOpen, setIsDialogOpen] = useState(true);

  const onPrimaryButtonClick = useCallback(() => {
    logTrace('system announcement done');
    setIsDialogOpen(false);
    onDone();
  }, [logTrace, onDone]);

  const onDialogDismiss = useCallback(() => {
    logTrace('system announcement dismiss');
    setIsDialogOpen(false);
    onDismiss();
  }, [logTrace, onDismiss]);

  return isDialogOpen && (
    <DialogWithMinHeight
      className="system-announcement"
      hidden={false}
      onDismiss={onDialogDismiss}
      dialogContentProps={{
        title,

        subTitle,
        showCloseButton: true,
      } as React.ComponentProps<typeof Dialog>['dialogContentProps']}
      minWidth={432}
      maxWidth={432}
      minHeight={172}
      modalProps={{
        isBlocking: true,

        dragOptions: false as unknown as NonNullable<
        React.ComponentProps<typeof Dialog>['modalProps']
        >['dragOptions'],
      }}
    >
      {content}
      <DialogFooter
        className={classes?.dialogFooter}
      >
        <PrimaryButton
          onClick={onPrimaryButtonClick}
          text={primaryButtonText}
        />
      </DialogFooter>
    </DialogWithMinHeight>
  );
};

SystemAnnouncementRaw.defaultProps = {
  subTitle: '',
  activity: {
    trace: _.noop,
    error: _.noop,
  },
};

const SystemAnnouncementWithHOC = _.compose(
  withActivityContext,
  withUserGovernance,
  (WrappedComponent: React.ComponentType<ISystemAnnouncementRawProps>) => withLock(WrappedComponent, {
    lockKey: keyVault.systemLevelAnnouncement,
    priority: priorityLevel.systemLevelAnnouncement,
  }),
  (WrappedComponent: React.ComponentType<ISystemAnnouncementRawProps>) => withDefaultStyles(WrappedComponent, getStyles)
)(SystemAnnouncementRaw);

export const SystemAnnouncement = (props: SystemAnnouncementProps) => (
  <Scenario name="SystemAnnouncement">
    <Activity name="SystemAnnouncement">
      <SystemAnnouncementWithHOC
        {...props}
        // Overwrite the default config to keep showing the system announcement for onDone()
        userGovernanceConfig={_.extend(defaultUserGovernanceConfig, {
          [PreferenceState.INIT]: {
            cooldownDays: 0,
            stateMachine: {
              onDone: PreferenceState.STOP_SHOWN,
              onDismiss: PreferenceState.STOP_SHOWN,
              onClose: PreferenceState.STOP_SHOWN,
            },
          },
        })}
      />
    </Activity>
  </Scenario>
);
